import { type FC, type ReactNode, useEffect } from "react";

import {
  CloseButton,
  Popover,
  PopoverButton,
  PopoverPanel,
} from "@headlessui/react";

import { Button } from "./button";
import { PillChevron } from "./pill_group";
import { SourceChecklist, type SourceOption } from "./source_checklist";

// Escape closes the open picker wherever focus is: a tick that saves disables
// its box for a moment, which drops focus out of the picker.
const CloseOnEscape: FC<{ close(): void }> = ({ close }) => {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close]);
  return null;
};

// A section's sources as a chip ("Intake ▾") that opens its checkboxes in place,
// floating over whatever box the chip sits in, under `title`. Done or Escape
// closes it; each tick is saved as he makes it.
export const SourcesPicker: FC<{
  label: string;
  title: string;
  options: SourceOption[];
  onToggle(value: string): void;
  disabled?: boolean;
  // Under the options, e.g. which supporting document.
  extra?: ReactNode;
  footer: ReactNode;
}> = ({ label, title, options, onToggle, disabled, extra, footer }) => (
  <Popover className="relative">
    <PopoverButton
      disabled={disabled}
      className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:border-violet-300 hover:text-violet-700 disabled:opacity-45"
    >
      {label}
      <PillChevron />
    </PopoverButton>
    <PopoverPanel
      anchor="bottom end"
      focus
      className="z-30 w-72 rounded-lg border border-slate-200 bg-white p-3 shadow-[0_12px_32px_rgba(15,23,42,0.14),0_2px_6px_rgba(15,23,42,0.06)] [--anchor-gap:4px] [--anchor-padding:8px]"
    >
      {({ close }) => (
        <>
          <CloseOnEscape close={close} />
          <p className="mb-2 text-[12.5px] font-semibold text-slate-800">
            {title}
          </p>
          <SourceChecklist
            options={options}
            onToggle={onToggle}
            disabled={disabled}
            extra={extra}
          />
          <div className="mt-3 space-y-0.5 border-t border-slate-100 pt-2 text-[11.5px] text-slate-500">
            {footer}
          </div>
          <div className="mt-3 flex justify-end">
            <CloseButton as={Button} type="button" color="navy" size="sm">
              Done
            </CloseButton>
          </div>
        </>
      )}
    </PopoverPanel>
  </Popover>
);
